import { ShieldCheck, ShieldX, X } from '@orgus/ui/icons';
import type { ChainVerifyResult } from '@orgus/protocol';
import { IconButton, RelativeTime, cn, formatInt } from '@orgus/ui';

/** The answer to "is the audit log intact?": icon plus label, what was checked, and what to do when it is not. */
export function VerifyResult({ r, onDismiss }: { r: ChainVerifyResult; onDismiss: () => void }) {
  const Icon = r.ok ? ShieldCheck : ShieldX;
  return (
    <div
      role="status"
      className={cn('flex items-start gap-3 rounded-card border px-4 py-3', r.ok ? 'border-status-success-line bg-status-success-soft' : 'border-status-danger-line bg-status-danger-soft')}
    >
      <Icon className={cn('mt-0.5 size-5 shrink-0', r.ok ? 'text-status-success' : 'text-status-danger')} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className={cn('text-body font-medium', r.ok ? 'text-status-success-ink' : 'text-status-danger-ink')}>{r.ok ? 'Audit chain intact' : 'Audit chain broken'}</p>
        <p className="text-ui text-fg-muted">{r.summary.replace(/^Chain intact: /, '').replace(/^\w/, (c) => c.toUpperCase())}</p>
        {!r.ok && (
          <p className="mt-1 text-ui text-fg-muted">
            Entries before the break still verify. Keep a copy of the hub folder for investigation (orgus backup), then follow docs/runbooks/incident-response.md.
          </p>
        )}
        <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-caption text-fg-subtle">
          <div>
            <dt className="inline">Live log </dt>
            <dd className="inline text-fg-muted num">{formatInt(r.live.entries)} entries</dd>
          </div>
          <div>
            <dt className="inline">Archive </dt>
            <dd className="inline text-fg-muted num">
              {formatInt(r.archive.entries)} entries in {r.archive.segments} {r.archive.segments === 1 ? 'segment' : 'segments'}
              {r.archive.prunedEntries ? `, ${formatInt(r.archive.prunedEntries)} pruned` : ''}
            </dd>
          </div>
          <div>
            <dt className="inline">Anchor </dt>
            <dd className="inline text-fg-muted">{r.live.anchorMatches == null ? 'none yet' : r.live.anchorMatches ? 'matches' : 'does not match'}</dd>
          </div>
          {r.live.head && (
            <div>
              <dt className="inline">Head </dt>
              <dd className="inline font-mono text-fg-muted" title={r.live.head}>
                {r.live.head.slice(0, 16)}
              </dd>
            </div>
          )}
          <div>
            <dt className="inline">Checked </dt>
            <dd className="inline text-fg-muted">
              <RelativeTime ts={r.checkedAt} />
            </dd>
          </div>
        </dl>
      </div>
      <IconButton label="Dismiss the verify result" icon={<X />} size="sm" onClick={onDismiss} />
    </div>
  );
}
